import React from 'react';
import { useAppNavigation } from '../hooks/useAppNavigation';

const DataDeletion = () => {
  const navigate = useAppNavigation();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-gray-900">Data Deletion Instructions</h1>
            <button
              onClick={() => navigate.to.HOME()}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              Back to App
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <div className="mb-8">
            <p className="text-gray-600">
              <strong>Effective Date:</strong> February 4, 2026<br />
              <strong>Last Updated:</strong> February 4, 2026
            </p>
          </div>

          <div className="prose prose-lg max-w-none">
            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">1. Your Right to Delete Your Data</h2>
            <p className="text-gray-700 mb-4">
              At ClutterLogix, we respect your privacy and your right to control your personal data. 
              You can request deletion of your account and all associated data at any time. This page 
              explains how to delete your data and what happens when you do.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">2. How to Delete Your Account</h2>
            
            <div className="bg-blue-50 border-l-4 border-blue-400 p-4 my-6">
              <p className="font-semibold text-blue-800">Option 1: Self-Service Deletion (Recommended)</p>
              <p className="text-blue-700">
                You can delete your account directly from the app:
              </p>
            </div>

            <ol className="list-decimal list-inside text-gray-700 mb-4 space-y-2">
              <li>Log in to your ClutterLogix account</li>
              <li>Go to <strong>Settings</strong> (gear icon)</li>
              <li>Scroll to the bottom and tap <strong>"Delete Account"</strong> (shown in red)</li>
              <li>Read the warning about what will be deleted</li>
              <li>Type <strong>DELETE</strong> to confirm</li>
              <li>Tap <strong>"Delete My Account"</strong> button</li>
            </ol>

            <p className="text-gray-700 mb-4">
              Your account and all data will be permanently deleted immediately.
            </p>

            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 my-6">
              <p className="font-semibold text-yellow-800">Option 2: Email Request</p>
              <p className="text-yellow-700">
                If you cannot access your account, you can request deletion by email:
              </p>
            </div>

            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
              <li>Send an email to: <strong>privacy@clutterlogix.com</strong></li>
              <li>Subject: "Account Deletion Request"</li>
              <li>Include the email address associated with your account</li>
              <li>We will verify your identity and process the request within 30 days</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">3. What Data Will Be Deleted</h2>
            <p className="text-gray-700 mb-4">
              When you delete your account, we permanently remove:
            </p>

            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
              <li><strong>Account Information:</strong> Email, name, profile picture, password</li>
              <li><strong>Inventory Data:</strong> All items, descriptions, tags, and values</li>
              <li><strong>Photos:</strong> All uploaded images and AI-generated content</li>
              <li><strong>Boxes:</strong> All box/container information and locations</li>
              <li><strong>Chat History:</strong> All AI assistant conversations</li>
              <li><strong>Reports:</strong> All generated reports and exports</li>
              <li><strong>Family Sharing:</strong> All family connections and shared access</li>
              <li><strong>Subscription Data:</strong> Payment history (Stripe retains billing records separately)</li>
            </ul>

            <div className="bg-red-50 border-l-4 border-red-400 p-4 my-6">
              <p className="font-semibold text-red-800">⚠️ Warning: This Action is Irreversible</p>
              <p className="text-red-700">
                Once your account is deleted, your data cannot be recovered. We recommend exporting 
                your inventory data before deletion if you want to keep a copy.
              </p>
            </div>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">4. Data Retention After Deletion</h2>
            <p className="text-gray-700 mb-4">
              After account deletion, we may retain certain data as required by law:
            </p>

            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
              <li><strong>Transaction Records:</strong> Payment records may be retained for tax and legal compliance (typically 7 years)</li>
              <li><strong>Anonymized Analytics:</strong> Aggregated, non-identifiable usage statistics</li>
              <li><strong>Legal Holds:</strong> Data subject to legal proceedings or investigations</li>
            </ul>

            <p className="text-gray-700 mb-4">
              All personally identifiable information is removed immediately upon deletion.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">5. Third-Party Data</h2>
            <p className="text-gray-700 mb-4">
              When you delete your account, we also request deletion from our service providers:
            </p>

            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
              <li><strong>Google Cloud Storage:</strong> All uploaded images are deleted</li>
              <li><strong>Heroku PostgreSQL:</strong> All database records are deleted</li>
              <li><strong>OpenAI:</strong> Does not retain data from API calls beyond 30 days</li>
              <li><strong>Stripe:</strong> Retains payment records per their retention policy</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">6. Google/Apple Sign-In Users</h2>
            <p className="text-gray-700 mb-4">
              If you signed up using Google or Apple Sign-In, deleting your ClutterLogix account will:
            </p>

            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
              <li>Remove all data stored in ClutterLogix</li>
              <li>Revoke ClutterLogix's access to your Google/Apple profile</li>
              <li>Not affect your Google or Apple account itself</li>
            </ul>

            <p className="text-gray-700 mb-4">
              You can also manage app permissions directly in your{' '}
              <a 
                href="https://myaccount.google.com/permissions" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                Google Account Settings
              </a>
              {' '}or{' '}
              <a 
                href="https://appleid.apple.com/account/manage" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                Apple ID Settings
              </a>.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">7. Contact Us</h2>
            <p className="text-gray-700 mb-2">For questions about data deletion:</p>
            
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
              <li><strong>Email:</strong> privacy@clutterlogix.com</li>
              <li><strong>Response Time:</strong> Within 30 days</li>
              <li><strong>Website:</strong> https://clutterlogix.com</li>
            </ul>

            <div className="mt-12 p-4 bg-gray-50 rounded-lg border-l-4 border-blue-500">
              <p className="text-sm text-gray-700 italic">
                This data deletion policy is part of our commitment to user privacy and compliance 
                with data protection regulations including GDPR and CCPA.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataDeletion;
